import { cva } from "class-variance-authority";
import { useEffect, useState } from "react";
import { TLUiToolItem, useEditor } from "tldraw";

import {
  BaseVariant,
  theme,
} from "@/components/canvas-editor/components/Container";
import { CustomToolInfo } from "@/components/canvas-editor/shapes/custom-node";
import {
  customNodeType,
  pinnableShapes,
} from "@/components/canvas-editor/shapes/shapes";
import { useIsMobile } from "@/components/hooks/use-mobile";
import { useActiveCustomTool } from "@/components/hooks/useCustomActiveTool";
import { cn } from "@/lib/utils";

const variants = cva(
  "border flex items-center gap-2 fixed top-0 left-0 p-3 rounded-lg z-50 pointer-events-none",
);

function ActiveCursor({
  activeCustomTool,
}: {
  activeCustomTool: TLUiToolItem<string, string>;
}) {
  const editor = useEditor();
  const editorTool = editor.getCurrentTool();
  const [defaultCursorVisible, setDefaultCursorVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState<
    | {
        x: number;
        y: number;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    const container = editor.getContainer();

    if (container instanceof HTMLElement) {
      let rect: DOMRect | undefined;

      function onMouseMove(e: MouseEvent) {
        if (!rect) {
          rect = container.getBoundingClientRect();
        }

        if (
          e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom
        ) {
          setMousePosition(undefined);
          return;
        }

        setMousePosition({
          x: e.clientX,
          y: e.clientY,
        });

        if (
          e.target instanceof HTMLElement &&
          e.target.closest(".tlui-layout__top__right")
        ) {
          return;
        }

        const selectedId = editor.getCurrentTool().id;
        const shouldHide =
          pinnableShapes.includes(selectedId) &&
          e.target instanceof HTMLElement &&
          e.target.closest(".canvas-sidebar");

        setDefaultCursorVisible(!shouldHide);
      }

      function onExitCanvas() {
        rect = undefined;
      }

      document.addEventListener("mousemove", onMouseMove);
      container.addEventListener("mouseleave", onExitCanvas);

      return () => {
        document.removeEventListener("mousemove", onMouseMove);
        container.removeEventListener("mouseleave", onExitCanvas);
      };
    }
  }, [editor]);

  useEffect(() => {
    const container = editor.getContainer();

    if (defaultCursorVisible) {
      container.classList.add("no-cursor");
    }

    return () => {
      container.classList.remove("no-cursor");
    };
  }, [defaultCursorVisible, editor]);

  useEffect(() => {
    if (!activeCustomTool) {
      return;
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        editor.setCurrentTool("select");
      }
    }

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeCustomTool, editor]);

  if (!mousePosition) {
    return null;
  }

  const color =
    (activeCustomTool.id === customNodeType &&
      (editorTool as unknown as { info?: CustomToolInfo }).info?.filter.metadata
        ?.color) ||
    (activeCustomTool as unknown as { defaultColor: BaseVariant | undefined })
      .defaultColor ||
    "default";

  const label =
    (activeCustomTool.id === customNodeType &&
      (editorTool as unknown as { info: CustomToolInfo }).info?.node.title) ||
    activeCustomTool.label;

  return (
    <div
      className={cn(
        theme.nodeCursor[color === "slate" ? "default" : color],
        variants(),
      )}
      style={{
        transform: `translate(-50%, -50%) translate(${mousePosition.x}px, ${mousePosition.y}px)`,
      }}
    >
      {/* @ts-expect-error types are hard */}
      <activeCustomTool.icon className="h-5 w-5 pointer-events-none" />
      {label}
    </div>
  );
}

export function NodeCursor() {
  const isMobile = useIsMobile();
  const activeCustomTool = useActiveCustomTool();

  if (!activeCustomTool || isMobile) {
    return null;
  }

  return <ActiveCursor activeCustomTool={activeCustomTool} />;
}
